// Fixed version of src/components/SEOStructuredData.jsx
import React from 'react';
import { Helmet } from 'react-helmet';

const SEOStructuredData = ({ currentView }) => {
  // Base structured data for webapp 
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Guilt Gauge",
    "description": "A calculator that helps users measure and manage their shopping guilt. Track impulse buys, emotional purchases, and get personalized recommendations.",
    "applicationCategory": "FinanceApplication",
    "operatingSystem": "Web",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "ratingCount": "235"
    },
    "keywords": "shopping guilt, impulse buying, buyer's remorse, retail therapy, shopping regret, emotional spending",
    "author": {
      "@type": "Organization",
      "name": "Guilt Gauge",
      "url": "https://guiltgauge.com"
    },
    "potentialAction": {
      "@type": "UseAction",
      "target": "https://guiltgauge.com"
    }
  };

  // Product schema for the web application
  const productSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Guilt Gauge Shopping Calculator",
    "applicationCategory": "LifestyleApplication",
    "operatingSystem": "Web",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "description": "Calculate your shopping guilt score and track spending habits. Reduce impulse buying and make mindful purchasing decisions.",
    "url": "https://guiltgauge.com",
    "contentRating": "G",
    "keywords": "shopping guilt, impulse buying, budgeting, shopping psychology, retail therapy, mindful spending, shopping addiction",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "ratingCount": "235",
      "bestRating": "5"
    }
  };

  // General article data
  const articleData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "How to Stop Feeling Guilty After Shopping",
    "description": "Learn how to track your shopping habits, reduce impulse purchases, and develop healthier spending patterns without the guilt.",
    "image": "https://guiltgauge.com/og-image.png",
    "author": {
      "@type": "Organization",
      "name": "Guilt Gauge",
      "url": "https://guiltgauge.com"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Guilt Gauge",
      "logo": {
        "@type": "ImageObject",
        "url": "https://guiltgauge.com/logo.png"
      }
    },
    "datePublished": "2023-05-01T00:00:00Z",
    "dateModified": "2025-05-01T00:00:00Z",
    "keywords": "shopping guilt, buyer's remorse, mindful shopping",
    "mainEntityOfPage": "https://guiltgauge.com",
    "relatedLink": [
      "https://guiltgauge.com/shopping-addiction",
      "https://guiltgauge.com/emotional-shopping-tracker"
    ]
  };

  // Shopping addiction article data
  const shoppingAddictionArticle = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Shopping Addiction: Signs, Causes, and Recovery Strategies",
    "description": "Learn the signs of compulsive buying disorder, understand its psychological causes, and discover effective recovery strategies and self-help tools.",
    "image": "https://guiltgauge.com/shopaholic-help.png",
    "author": {
      "@type": "Organization",
      "name": "Guilt Gauge",
      "url": "https://guiltgauge.com"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Guilt Gauge",
      "logo": {
        "@type": "ImageObject",
        "url": "https://guiltgauge.com/logo.png"
      }
    },
    "datePublished": "2023-09-15T00:00:00Z",
    "dateModified": "2025-05-01T00:00:00Z",
    "keywords": "shopping addiction, compulsive buying disorder, shopaholic, spending addiction",
    "mainEntityOfPage": "https://guiltgauge.com/shopping-addiction",
    "relatedLink": [
      "https://guiltgauge.com/shopaholic-test",
      "https://guiltgauge.com/shopping-addiction-stories"
    ]
  };

  // Fashion shopping guilt article
  const fashionGuiltArticle = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Overcome Fashion Shopping Guilt with Mindful Spending",
    "description": "Learn how to track fashion purchases and shop for clothes without guilt. Get tips for mindful fashion spending.",
    "image": "https://guiltgauge.com/fashion-guilt.png",
    "author": {
      "@type": "Organization",
      "name": "Guilt Gauge",
      "url": "https://guiltgauge.com"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Guilt Gauge",
      "logo": {
        "@type": "ImageObject",
        "url": "https://guiltgauge.com/logo.png"
      }
    },
    "datePublished": "2024-01-15T00:00:00Z",
    "dateModified": "2025-05-01T00:00:00Z",
    "keywords": "fashion shopping guilt, mindful fashion shopping, buyer's remorse clothing",
    "mainEntityOfPage": "https://guiltgauge.com/fashion-shopping-guilt",
    "relatedLink": [
      "https://guiltgauge.com/emotional-shopping-tracker",
      "https://guiltgauge.com/shopping-addiction"
    ]
  };

  // Tech shopping guilt article
  const techGuiltArticle = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Tech Shopping Guilt: Shop Smarter for Gadgets",
    "description": "Calculate guilt scores for tech purchases and avoid buyer's remorse with mindful shopping strategies.",
    "image": "https://guiltgauge.com/tech-guilt.png",
    "author": {
      "@type": "Organization",
      "name": "Guilt Gauge",
      "url": "https://guiltgauge.com"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Guilt Gauge",
      "logo": {
        "@type": "ImageObject",
        "url": "https://guiltgauge.com/logo.png"
      }
    },
    "datePublished": "2024-02-01T00:00:00Z",
    "dateModified": "2025-05-01T00:00:00Z",
    "keywords": "tech shopping guilt, mindful tech shopping, buyer's remorse tech",
    "mainEntityOfPage": "https://guiltgauge.com/tech-shopping-guilt",
    "relatedLink": [
      "https://guiltgauge.com/impulse-buying",
      "https://guiltgauge.com/shopping-psychology"
    ]
  };

  // Luxury shopping guilt article
  const luxuryGuiltArticle = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Managing Luxury Shopping Guilt",
    "description": "Track luxury purchases and shop high-end items mindfully to avoid guilt and buyer's remorse.",
    "image": "https://guiltgauge.com/luxury-guilt.png",
    "author": {
      "@type": "Organization",
      "name": "Guilt Gauge",
      "url": "https://guiltgauge.com"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Guilt Gauge",
      "logo": {
        "@type": "ImageObject",
        "url": "https://guiltgauge.com/logo.png"
      }
    },
    "datePublished": "2024-03-01T00:00:00Z",
    "dateModified": "2025-05-01T00:00:00Z",
    "keywords": "luxury shopping guilt, mindful luxury shopping, buyer's remorse luxury",
    "mainEntityOfPage": "https://guiltgauge.com/luxury-shopping-guilt",
    "relatedLink": [
      "https://guiltgauge.com/shopping-psychology",
      "https://guiltgauge.com/emotional-shopping-tracker"
    ]
  };

  // Home decor remorse article
  const homeDecorRemorseArticle = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Overcome Home Decor Buyer's Remorse",
    "description": "Track home decor purchases and shop mindfully to avoid buyer's remorse and create a guilt-free home.",
    "image": "https://guiltgauge.com/home-decor-remorse.png",
    "author": {
      "@type": "Organization",
      "name": "Guilt Gauge",
      "url": "https://guiltgauge.com"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Guilt Gauge",
      "logo": {
        "@type": "ImageObject",
        "url": "https://guiltgauge.com/logo.png"
      }
    },
    "datePublished": "2024-04-01T00:00:00Z",
    "dateModified": "2025-05-01T00:00:00Z",
    "keywords": "home decor remorse, mindful home shopping, buyer's remorse home decor",
    "mainEntityOfPage": "https://guiltgauge.com/home-decor-remorse",
    "relatedLink": [
      "https://guiltgauge.com/no-spend-challenge",
      "https://guiltgauge.com/budget-shopping"
    ]
  };

  // Holiday shopping guilt article
  const holidayGuiltArticle = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Holiday Shopping Guilt: Stress-Free Spending Tips",
    "description": "Track holiday shopping and reduce guilt with mindful spending strategies for the festive season.",
    "image": "https://guiltgauge.com/holiday-guilt.png",
    "author": {
      "@type": "Organization",
      "name": "Guilt Gauge",
      "url": "https://guiltgauge.com"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Guilt Gauge",
      "logo": {
        "@type": "ImageObject",
        "url": "https://guiltgauge.com/logo.png"
      }
    },
    "datePublished": "2024-05-01T00:00:00Z",
    "dateModified": "2025-05-01T00:00:00Z",
    "keywords": "holiday shopping guilt, mindful holiday shopping, buyer's remorse holiday",
    "mainEntityOfPage": "https://guiltgauge.com/holiday-shopping-guilt",
    "relatedLink": [
      "https://guiltgauge.com/black-friday-remorse",
      "https://guiltgauge.com/budget-shopping"
    ]
  };

  // Black Friday remorse article
  const blackFridayRemorseArticle = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Avoid Black Friday Buyer's Remorse",
    "description": "Shop Black Friday deals mindfully and avoid buyer's remorse with our guilt tracking tools.",
    "image": "https://guiltgauge.com/black-friday-remorse.png",
    "author": {
      "@type": "Organization",
      "name": "Guilt Gauge",
      "url": "https://guiltgauge.com"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Guilt Gauge",
      "logo": {
        "@type": "ImageObject",
        "url": "https://guiltgauge.com/logo.png"
      }
    },
    "datePublished": "2024-06-01T00:00:00Z",
    "dateModified": "2025-05-01T00:00:00Z",
    "keywords": "black friday remorse, mindful deal shopping, buyer's remorse black friday",
    "mainEntityOfPage": "https://guiltgauge.com/black-friday-remorse",
    "relatedLink": [
      "https://guiltgauge.com/holiday-shopping-guilt",
      "https://guiltgauge.com/impulse-buying"
    ]
  };

  // Birthday splurge article
  const birthdaySplurgeArticle = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Birthday Splurge Without the Guilt",
    "description": "Calculate guilt for birthday splurges and shop for celebrations mindfully with our tools.",
    "image": "https://guiltgauge.com/birthday-splurge.png",
    "author": {
      "@type": "Organization",
      "name": "Guilt Gauge",
      "url": "https://guiltgauge.com"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Guilt Gauge",
      "logo": {
        "@type": "ImageObject",
        "url": "https://guiltgauge.com/logo.png"
      }
    },
    "datePublished": "2024-07-01T00:00:00Z",
    "dateModified": "2025-05-01T00:00:00Z",
    "keywords": "birthday splurge guilt, mindful birthday shopping, buyer's remorse birthday",
    "mainEntityOfPage": "https://guiltgauge.com/birthday-splurge",
    "relatedLink": [
      "https://guiltgauge.com/emotional-shopping-tracker",
      "https://guiltgauge.com/impulse-buying"
    ]
  };

  // Student shopping guilt article
  const studentGuiltArticle = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Student Shopping Guilt: Budgeting Tips for Students",
    "description": "Track student shopping and reduce guilt with budgeting tips for mindful spending on a student budget.",
    "image": "https://guiltgauge.com/student-guilt.png",
    "author": {
      "@type": "Organization",
      "name": "Guilt Gauge",
      "url": "https://guiltgauge.com"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Guilt Gauge",
      "logo": {
        "@type": "ImageObject",
        "url": "https://guiltgauge.com/logo.png"
      }
    },
    "datePublished": "2024-08-01T00:00:00Z",
    "dateModified": "2025-05-01T00:00:00Z",
    "keywords": "student shopping guilt, mindful student shopping, buyer's remorse student",
    "mainEntityOfPage": "https://guiltgauge.com/student-shopping-guilt",
    "relatedLink": [
      "https://guiltgauge.com/budget-shopping",
      "https://guiltgauge.com/no-spend-challenge"
    ]
  };

  // Parent shopping guilt article
  const parentGuiltArticle = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Parent Shopping Guilt: Shop for Your Family Without Regret",
    "description": "Manage parent shopping guilt and shop for your family mindfully with our guilt tracking tools.",
    "image": "https://guiltgauge.com/parent-guilt.png",
    "author": {
      "@type": "Organization",
      "name": "Guilt Gauge",
      "url": "https://guiltgauge.com"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Guilt Gauge",
      "logo": {
        "@type": "ImageObject",
        "url": "https://guiltgauge.com/logo.png"
      }
    },
    "datePublished": "2024-09-01T00:00:00Z",
    "dateModified": "2025-05-01T00:00:00Z",
    "keywords": "parent shopping guilt, mindful family shopping, buyer's remorse parent",
    "mainEntityOfPage": "https://guiltgauge.com/parent-shopping-guilt",
    "relatedLink": [
      "https://guiltgauge.com/shopping-psychology",
      "https://guiltgauge.com/budget-shopping"
    ]
  };

  // Retirement shopping article
  const retirementShoppingArticle = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Retirement Shopping: Mindful Spending in Retirement",
    "description": "Track retirement shopping and avoid guilt with mindful spending strategies for retirees.",
    "image": "https://guiltgauge.com/retirement-shopping.png",
    "author": {
      "@type": "Organization",
      "name": "Guilt Gauge",
      "url": "https://guiltgauge.com"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Guilt Gauge",
      "logo": {
        "@type": "ImageObject",
        "url": "https://guiltgauge.com/logo.png"
      }
    },
    "datePublished": "2024-10-01T00:00:00Z",
    "dateModified": "2025-05-01T00:00:00Z",
    "keywords": "retirement shopping guilt, mindful retirement shopping, buyer's remorse retirement",
    "mainEntityOfPage": "https://guiltgauge.com/retirement-shopping",
    "relatedLink": [
      "https://guiltgauge.com/budget-shopping",
      "https://guiltgauge.com/shopping-guilt-vs-anxiety"
    ]
  };

  // Debt shopping article
  const debtShoppingArticle = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Shopping with Debt: Manage Guilt and Spending",
    "description": "Shop mindfully while managing debt and reduce guilt with our tracking tools and strategies.",
    "image": "https://guiltgauge.com/debt-shopping.png",
    "author": {
      "@type": "Organization",
      "name": "Guilt Gauge",
      "url": "https://guiltgauge.com"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Guilt Gauge",
      "logo": {
        "@type": "ImageObject",
        "url": "https://guiltgauge.com/logo.png"
      }
    },
    "datePublished": "2024-11-01T00:00:00Z",
    "dateModified": "2025-05-01T00:00:00Z",
    "keywords": "debt shopping guilt, mindful debt shopping, buyer's remorse debt",
    "mainEntityOfPage": "https://guiltgauge.com/debt-shopping",
    "relatedLink": [
      "https://guiltgauge.com/shopping-guilt-vs-anxiety",
      "https://guiltgauge.com/budget-shopping"
    ]
  };

  // Budget shopping article
  const budgetShoppingArticle = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Budget Shopping: Shop Guilt-Free on a Budget",
    "description": "Stick to your budget and shop guilt-free with personalized budgeting tips and tracking tools.",
    "image": "https://guiltgauge.com/budget-shopping.png",
    "author": {
      "@type": "Organization",
      "name": "Guilt Gauge",
      "url": "https://guiltgauge.com"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Guilt Gauge",
      "logo": {
        "@type": "ImageObject",
        "url": "https://guiltgauge.com/logo.png"
      }
    },
    "datePublished": "2024-12-01T00:00:00Z",
    "dateModified": "2025-05-01T00:00:00Z",
    "keywords": "budget shopping guilt, mindful budget shopping, buyer's remorse budget",
    "mainEntityOfPage": "https://guiltgauge.com/budget-shopping",
    "relatedLink": [
      "https://guiltgauge.com/no-spend-challenge",
      "https://guiltgauge.com/student-shopping-guilt"
    ]
  };

  // Post-payday shopping article
  const postPaydayArticle = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Post-Payday Shopping: Avoid Guilt After Payday",
    "description": "Track post-payday shopping and avoid guilt with mindful spending tips for after payday.",
    "image": "https://guiltgauge.com/post-payday.png",
    "author": {
      "@type": "Organization",
      "name": "Guilt Gauge",
      "url": "https://guiltgauge.com"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Guilt Gauge",
      "logo": {
        "@type": "ImageObject",
        "url": "https://guiltgauge.com/logo.png"
      }
    },
    "datePublished": "2025-01-01T00:00:00Z",
    "dateModified": "2025-05-01T00:00:00Z",
    "keywords": "post payday shopping guilt, mindful payday shopping, buyer's remorse payday",
    "mainEntityOfPage": "https://guiltgauge.com/post-payday-shopping",
    "relatedLink": [
      "https://guiltgauge.com/impulse-buying",
      "https://guiltgauge.com/budget-shopping"
    ]
  };

  // Emotional shopping article
  const emotionalShoppingArticle = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Emotional Shopping: Track and Manage Triggers",
    "description": "Track emotional purchases and understand triggers to shop mindfully and reduce guilt.",
    "image": "https://guiltgauge.com/emotional-shopping.png",
    "author": {
      "@type": "Organization",
      "name": "Guilt Gauge",
      "url": "https://guiltgauge.com"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Guilt Gauge",
      "logo": {
        "@type": "ImageObject",
        "url": "https://guiltgauge.com/logo.png"
      }
    },
    "datePublished": "2025-02-01T00:00:00Z",
    "dateModified": "2025-05-01T00:00:00Z",
    "keywords": "emotional shopping guilt, mindful emotional shopping, buyer's remorse emotional",
    "mainEntityOfPage": "https://guiltgauge.com/emotional-shopping-tracker",
    "relatedLink": [
      "https://guiltgauge.com/retail-therapy-vs-addiction",
      "https://guiltgauge.com/shopping-psychology"
    ]
  };

  // Impulse buying article
  const impulseBuyingArticle = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Overcome Impulse Buying Guilt",
    "description": "Overcome impulse buying with guilt tracking tools and personalized mindful shopping tips.",
    "image": "https://guiltgauge.com/impulse-buying.png",
    "author": {
      "@type": "Organization",
      "name": "Guilt Gauge",
      "url": "https://guiltgauge.com"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Guilt Gauge",
      "logo": {
        "@type": "ImageObject",
        "url": "https://guiltgauge.com/logo.png"
      }
    },
    "datePublished": "2025-03-01T00:00:00Z",
    "dateModified": "2025-05-01T00:00:00Z",
    "keywords": "impulse buying guilt, mindful impulse shopping, buyer's remorse impulse",
    "mainEntityOfPage": "https://guiltgauge.com/impulse-buying",
    "relatedLink": [
      "https://guiltgauge.com/fomo-shopping",
      "https://guiltgauge.com/emotional-shopping-tracker"
    ]
  };

  // FOMO shopping article
  const fomoShoppingArticle = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "FOMO Shopping: Avoid Guilt from Fear of Missing Out",
    "description": "Calculate guilt for FOMO-driven purchases and shop mindfully to avoid regret.",
    "image": "https://guiltgauge.com/fomo-shopping.png",
    "author": {
      "@type": "Organization",
      "name": "Guilt Gauge",
      "url": "https://guiltgauge.com"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Guilt Gauge",
      "logo": {
        "@type": "ImageObject",
        "url": "https://guiltgauge.com/logo.png"
      }
    },
    "datePublished": "2025-04-01T00:00:00Z",
    "dateModified": "2025-05-01T00:00:00Z",
    "keywords": "fomo shopping guilt, mindful fomo shopping, buyer's remorse fomo",
    "mainEntityOfPage": "https://guiltgauge.com/fomo-shopping",
    "relatedLink": [
      "https://guiltgauge.com/impulse-buying",
      "https://guiltgauge.com/emotional-shopping-tracker"
    ]
  };

  // Late-night shopping article
  const lateNightShoppingArticle = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Late Night Shopping: Reduce Guilt from Impulse Buys",
    "description": "Track late-night shopping and reduce guilt with tips for mindful purchasing decisions.",
    "image": "https://guiltgauge.com/late-night-shopping.png",
    "author": {
      "@type": "Organization",
      "name": "Guilt Gauge",
      "url": "https://guiltgauge.com"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Guilt Gauge",
      "logo": {
        "@type": "ImageObject",
        "url": "https://guiltgauge.com/logo.png"
      }
    },
    "datePublished": "2025-05-01T00:00:00Z",
    "dateModified": "2025-05-01T00:00:00Z",
    "keywords": "late night shopping guilt, mindful late night shopping, buyer's remorse late night",
    "mainEntityOfPage": "https://guiltgauge.com/late-night-shopping",
    "relatedLink": [
      "https://guiltgauge.com/emotional-shopping-tracker",
      "https://guiltgauge.com/impulse-buying"
    ]
  };

  // No-spend challenge article
  const noSpendChallengeArticle = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "No-Spend Challenge: Build Mindful Spending Habits",
    "description": "Join our no-spend challenge to reduce shopping guilt and develop mindful spending habits.",
    "image": "https://guiltgauge.com/no-spend-challenge.png",
    "author": {
      "@type": "Organization",
      "name": "Guilt Gauge",
      "url": "https://guiltgauge.com"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Guilt Gauge",
      "logo": {
        "@type": "ImageObject",
        "url": "https://guiltgauge.com/logo.png"
      }
    },
    "datePublished": "2025-05-01T00:00:00Z",
    "dateModified": "2025-05-01T00:00:00Z",
    "keywords": "no spend challenge, mindful spending challenge, shopping guilt challenge",
    "mainEntityOfPage": "https://guiltgauge.com/no-spend-challenge",
    "relatedLink": [
      "https://guiltgauge.com/shopping-detox",
      "https://guiltgauge.com/budget-shopping"
    ]
  };

  // Shopping detox article
  const shoppingDetoxArticle = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Shopping Detox: Overcome Compulsive Buying",
    "description": "Start a shopping detox to overcome guilt and compulsive buying with personalized recovery tips.",
    "image": "https://guiltgauge.com/shopping-detox.png",
    "author": {
      "@type": "Organization",
      "name": "Guilt Gauge",
      "url": "https://guiltgauge.com"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Guilt Gauge",
      "logo": {
        "@type": "ImageObject",
        "url": "https://guiltgauge.com/logo.png"
      }
    },
    "datePublished": "2025-05-01T00:00:00Z",
    "dateModified": "2025-05-01T00:00:00Z",
    "keywords": "shopping detox, compulsive buying detox, shopping guilt detox",
    "mainEntityOfPage": "https://guiltgauge.com/shopping-detox",
    "relatedLink": [
      "https://guiltgauge.com/no-spend-challenge",
      "https://guiltgauge.com/shopping-addiction"
    ]
  };

  // Shopping addiction stories article
    const addictionStoriesArticle = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "Shopping Addiction Recovery Stories",
      "description": "Read inspiring shopping addiction recovery stories and learn strategies to shop mindfully.",
      "image": "https://guiltgauge.com/addiction-stories.png",
      "author": {
        "@type": "Organization",
        "name": "Guilt Gauge",
        "url": "https://guiltgauge.com"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Guilt Gauge",
        "logo": {
          "@type": "ImageObject",
          "url": "https://guiltgauge.com/logo.png"
        }
      },
      "datePublished": "2025-05-01T00:00:00Z",
      "dateModified": "2025-05-01T00:00:00Z",
      "keywords": "shopping addiction stories, compulsive buying recovery, shopping guilt recovery",
      "mainEntityOfPage": "https://guiltgauge.com/shopping-addiction-stories",
      "relatedLink": [
        "https://guiltgauge.com/shopping-addiction",
        "https://guiltgauge.com/shopaholic-test"
      ]
    };
  
    // Shopping guilt vs financial anxiety article
    const guiltVsAnxietyArticle = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "Shopping Guilt vs Financial Anxiety: Understand and Manage",
      "description": "Understand shopping guilt vs financial anxiety and manage both with mindful shopping tools.",
      "image": "https://guiltgauge.com/guilt-vs-anxiety.png",
      "author": {
        "@type": "Organization",
        "name": "Guilt Gauge",
        "url": "https://guiltgauge.com"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Guilt Gauge",
        "logo": {
          "@type": "ImageObject",
          "url": "https://guiltgauge.com/logo.png"
        }
      },
      "datePublished": "2025-05-01T00:00:00Z",
      "dateModified": "2025-05-01T00:00:00Z",
      "keywords": "shopping guilt vs anxiety, financial anxiety shopping, buyer's remorse anxiety",
      "mainEntityOfPage": "https://guiltgauge.com/shopping-guilt-vs-anxiety",
      "relatedLink": [
        "https://guiltgauge.com/debt-shopping",
        "https://guiltgauge.com/shopping-psychology"
      ]
    };
  
    // Retail therapy vs addiction article
    const retailTherapyArticle = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "Retail Therapy vs Shopping Addiction: Know the Difference",
      "description": "Learn the difference between retail therapy and shopping addiction with tips for healthy spending.",
      "image": "https://guiltgauge.com/retail-therapy.png",
      "author": {
        "@type": "Organization",
        "name": "Guilt Gauge",
        "url": "https://guiltgauge.com"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Guilt Gauge",
        "logo": {
          "@type": "ImageObject",
          "url": "https://guiltgauge.com/logo.png"
        }
      },
      "datePublished": "2025-05-01T00:00:00Z",
      "dateModified": "2025-05-01T00:00:00Z",
      "keywords": "retail therapy vs addiction, shopping addiction guide, mindful retail therapy",
      "mainEntityOfPage": "https://guiltgauge.com/retail-therapy-vs-addiction",
      "relatedLink": [
        "https://guiltgauge.com/emotional-shopping-tracker",
        "https://guiltgauge.com/shopping-addiction"
      ]
    };
  
    // Buyer's remorse timeline article
    const remorseTimelineArticle = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "Buyer's Remorse Timeline: Shop Without Regret",
      "description": "Explore the buyer's remorse timeline and learn how to shop without regret with mindful strategies.",
      "image": "https://guiltgauge.com/remorse-timeline.png",
      "author": {
        "@type": "Organization",
        "name": "Guilt Gauge",
        "url": "https://guiltgauge.com"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Guilt Gauge",
        "logo": {
          "@type": "ImageObject",
          "url": "https://guiltgauge.com/logo.png"
        }
      },
      "datePublished": "2025-05-01T00:00:00Z",
      "dateModified": "2025-05-01T00:00:00Z",
      "keywords": "buyer's remorse timeline, shopping guilt timeline, mindful shopping guide",
      "mainEntityOfPage": "https://guiltgauge.com/buyers-remorse-timeline",
      "relatedLink": [
        "https://guiltgauge.com/shopping-psychology",
        "https://guiltgauge.com/emotional-shopping-tracker"
      ]
    };

    const bnplArticle = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "Buy Now, Pay Later Services: Hidden Costs & Psychological Tricks",
      "description": "Learn about the true costs of Buy Now, Pay Later services like Affirm, Klarna, and Afterpay, including hidden fees, psychological manipulation, and debt risks.",
      "image": "https://guiltgauge.com/bnpl-explained.png",
      "author": {
        "@type": "Organization",
        "name": "Guilt Gauge",
        "url": "https://guiltgauge.com"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Guilt Gauge",
        "logo": {
          "@type": "ImageObject",
          "url": "https://guiltgauge.com/logo.png"
        }
      },
      "datePublished": "2025-05-01T00:00:00Z",
      "dateModified": "2025-05-01T00:00:00Z",
      "keywords": "buy now pay later, Affirm, Klarna, Afterpay, BNPL risks, payment plans, consumer debt, shopping psychology",
      "mainEntityOfPage": "https://guiltgauge.com/buy-now-pay-later",
      "relatedLink": [
        "https://guiltgauge.com/debt-shopping",
        "https://guiltgauge.com/emotional-shopping-tracker"
      ]
    };
  
    // Quiz schema for shopaholic test
    const testSchema = {
      "@context": "https://schema.org",
      "@type": "Quiz",
      "name": "Shopping Addiction Assessment Test",
      "description": "A free self-assessment to determine if your shopping habits indicate compulsive buying disorder.",
      "about": {
        "@type": "Thing",
        "name": "Compulsive Buying Disorder"
      },
      "timeRequired": "PT5M",
      "publisher": {
        "@type": "Organization",
        "name": "Guilt Gauge",
        "url": "https://guiltgauge.com"
      }
    };
  
    // Medical condition data for shopping addiction
    const medicalConditionData = {
      "@context": "https://schema.org",
      "@type": "MedicalCondition",
      "name": "Compulsive Buying Disorder",
      "alternateName": ["Shopping Addiction", "Oniomania"],
      "description": "A behavioral addiction characterized by excessive, impulsive shopping despite negative consequences.",
      "possibleSymptom": [
        "Preoccupation with shopping",
        "Shopping to cope with emotions",
        "Guilt or shame after purchases",
        "Financial difficulties"
      ],
      "possibleTreatment": [
        {
          "@type": "TherapeuticProcedure",
          "name": "Cognitive Behavioral Therapy"
        },
        {
          "@type": "TherapeuticProcedure",
          "name": "Self-monitoring tools (like Guilt Gauge)"
        }
      ]
    };
  
    // FAQ questions for general shopping guilt
    const generalFAQQuestions = [
      {
        "@type": "Question",
        "name": "Why do I feel guilty after shopping?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Shopping guilt often stems from spending beyond your budget, emotional purchases, or buying items that don't align with your values. Track your habits with Guilt Gauge to shop mindfully."
        }
      },
      {
        "@type": "Question",
        "name": "How can I avoid buyer's remorse?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Avoid buyer's remorse by practicing mindful shopping, setting a budget, and waiting 24 hours before major purchases. Use Guilt Gauge to track your spending patterns."
        }
      },
      {
        "@type": "Question",
        "name": "Is it normal to feel guilty after shopping?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, shopping guilt is very common. Studies show that up to 80% of consumers experience post-purchase regret, especially with unplanned or emotional purchases."
        }
      },
      {
        "@type": "Question",
        "name": "How can I make better shopping decisions?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Make better shopping decisions by tracking your purchases, understanding your emotional triggers, implementing a waiting period for non-essentials, and creating a budget for guilt-free spending."
        }
      }
    ];
  
    // FAQ questions for shopping addiction
    const addictionFAQQuestions = [
      {
        "@type": "Question",
        "name": "What are signs of shopping addiction?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Signs include shopping to cope with emotions, hiding purchases, spending beyond means, and feeling anxious when not shopping. Take our free shopaholic test for insights."
        }
      },
      {
        "@type": "Question",
        "name": "How can I overcome shopping addiction?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Overcome shopping addiction by tracking habits, identifying triggers, setting budgets, and using tools like Guilt Gauge. Consider professional help for severe cases."
        }
      },
      {
        "@type": "Question",
        "name": "Is shopping addiction a real condition?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, compulsive buying disorder is recognized by mental health professionals as a behavioral addiction characterized by uncontrollable urges to shop despite negative consequences."
        }
      },
      {
        "@type": "Question",
        "name": "How do I know if I'm a shopaholic?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "You may be a shopaholic if you shop to feel better, hide purchases, experience guilt or shame afterward, accumulate unused items, or face financial problems due to shopping. Take our assessment test for personalized insights."
        }
      }
    ];
  
    // Create general FAQPage schema
    const generalFAQPageSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": generalFAQQuestions
    };
  
    // Create addiction FAQPage schema
    const addictionFAQPageSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": addictionFAQQuestions
    };
  
    // Get the current URL path if in browser environment
    const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
    
    // Handle the special case for shopping-addiction-test explicitly
    if (pathname === "/shopping-addiction-test" || currentView === "shopaholic-test") {
      return (
        <Helmet>
          <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
          <script type="application/ld+json">{JSON.stringify(productSchema)}</script>
          <script type="application/ld+json">{JSON.stringify(testSchema)}</script>
          <script type="application/ld+json">{JSON.stringify(addictionFAQPageSchema)}</script>
          <script type="application/ld+json">{JSON.stringify(medicalConditionData)}</script>
        </Helmet>
      );
    }
  
    // Determine which schemas to render based on current view for all other pages
    const selectSchemas = () => {
      // Base schemas included for all pages
      const schemas = [structuredData, productSchema];
      
      // Select the appropriate article schema based on view
      let articleSchema = null;
      let faqSchema = null; // Set to null by default
      
      // First, set article schema based on current view
      switch (currentView) {
        case "landing":
          articleSchema = articleData;
          faqSchema = generalFAQPageSchema;
          break;
        case "shopaholic":
        case "shopping-addiction":
          articleSchema = shoppingAddictionArticle;
          // Fix for shopping-addiction: Only use addiction FAQ
          faqSchema = addictionFAQPageSchema;
          // Add medical condition data
          schemas.push(medicalConditionData);
          break;
        case "fashion-guilt":
          articleSchema = fashionGuiltArticle;
          faqSchema = generalFAQPageSchema;
          break;
        case "tech-guilt":
          articleSchema = techGuiltArticle;
          faqSchema = generalFAQPageSchema;
          break;
        case "luxury-guilt":
          articleSchema = luxuryGuiltArticle;
          faqSchema = generalFAQPageSchema;
          break;
        case "home-decor-remorse":
          articleSchema = homeDecorRemorseArticle;
          faqSchema = generalFAQPageSchema;
          break;
        case "holiday-guilt":
          articleSchema = holidayGuiltArticle;
          faqSchema = generalFAQPageSchema;
          break;
        case "black-friday-remorse":
          articleSchema = blackFridayRemorseArticle;
          faqSchema = generalFAQPageSchema;
          break;
        case "birthday-splurge":
          articleSchema = birthdaySplurgeArticle;
          faqSchema = generalFAQPageSchema;
          break;
        case "student-guilt":
          articleSchema = studentGuiltArticle;
          faqSchema = generalFAQPageSchema;
          break;
        case "parent-guilt":
          articleSchema = parentGuiltArticle;
          faqSchema = generalFAQPageSchema;
          break;
        case "retirement-shopping":
          articleSchema = retirementShoppingArticle;
          faqSchema = generalFAQPageSchema;
          break;
        case "debt-shopping":
          articleSchema = debtShoppingArticle;
          faqSchema = generalFAQPageSchema;
          break;
        case "budget-shopping":
          articleSchema = budgetShoppingArticle;
          faqSchema = generalFAQPageSchema;
          break;
        case "post-payday":
          articleSchema = postPaydayArticle;
          faqSchema = generalFAQPageSchema;
          break;
        case "emotional-shopping":
          articleSchema = emotionalShoppingArticle;
          faqSchema = generalFAQPageSchema;
          break;
        case "impulse-buying":
          articleSchema = impulseBuyingArticle;
          faqSchema = generalFAQPageSchema;
          break;
        case "fomo-shopping":
          articleSchema = fomoShoppingArticle;
          faqSchema = generalFAQPageSchema;
          break;
        case "late-night-shopping":
          articleSchema = lateNightShoppingArticle;
          faqSchema = generalFAQPageSchema;
          break;
        case "no-spend-challenge":
          articleSchema = noSpendChallengeArticle;
          faqSchema = generalFAQPageSchema;
          break;
        case "shopping-detox":
          articleSchema = shoppingDetoxArticle;
          faqSchema = addictionFAQPageSchema; // Use addiction FAQ for this
          break;
        case "addiction-stories":
          articleSchema = addictionStoriesArticle;
          faqSchema = addictionFAQPageSchema; // Use addiction FAQ for this
          break;
        case "guilt-vs-anxiety":
          articleSchema = guiltVsAnxietyArticle;
          faqSchema = generalFAQPageSchema;
          break;
        case "retail-therapy":
          articleSchema = retailTherapyArticle;
          faqSchema = addictionFAQPageSchema; // Use addiction FAQ for this
          break;
        case "remorse-timeline":
          articleSchema = remorseTimelineArticle;
          faqSchema = generalFAQPageSchema;
          break;
        case "shareable-infographics":
          articleSchema = articleData;
          faqSchema = generalFAQPageSchema;
          break;
        case "bnpl-guilt":
          articleSchema = bnplArticle;
          faqSchema = generalFAQPageSchema;
          break;
        default:
          articleSchema = articleData;
          faqSchema = generalFAQPageSchema;
      }
  
      // Build the final array of schemas to render
      if (articleSchema) schemas.push(articleSchema);
      if (faqSchema) schemas.push(faqSchema); // Only add the FAQ if it's set
      
      return schemas;
    };
  
    // Get all schemas to render for normal pages
    const schemasToRender = selectSchemas();
  
    return (
      <Helmet>
        {schemasToRender.map((schema, index) => (
          <script key={index} type="application/ld+json">
            {JSON.stringify(schema)}
          </script>
        ))}
      </Helmet>
    );
  };
  
  export default SEOStructuredData;
